import DOMPurify from "dompurify";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { SettingsIconButton } from "../../../../../components/button/SettingsIconButton";
import RichText from "../../../../../components/richtext/RichText";
import { CheckSvg } from "../../../../../components/svg/CheckSvg";
import { CloseSvg } from "../../../../../components/svg/CloseSvg";
import { DraggableSvg } from "../../../../../components/svg/DraggableSvg";
import { WorkTemplateTaskModel } from "../../../../../factories/templates/models/template.model";
import {
  useAddSubtaskMutation,
  useUpdateSubtaskMutation,
} from "../../../../../slices/TemplatesSlice";
import { serverErrorMessage } from "../../../workTypesV2/model/workTypeEditor.utils";
import {
  SUBTASK_TITLE_MAX,
  subtaskTitleProblem,
} from "../model/taskTemplateEditor.utils";

type EditableSubtaskValue = {
  id?: string;
  name: string;
  description: string;
};

type Props = {
  defaultValue?: WorkTemplateTaskModel | EditableSubtaskValue;
  order: number;
  onClose: () => void;
  getTemplate?: () => Promise<void>;
  isLocalMode?: boolean;
  onSubmitLocal?: (data: EditableSubtaskValue) => void;
};

type FormData = {
  name: string;
  description: string;
};

export const SubtaskForm: React.FC<Props> = ({
  defaultValue,
  order,
  onClose,
  getTemplate,
  isLocalMode = false,
  onSubmitLocal,
}) => {
  const { id } = useParams();

  const { control, handleSubmit, reset, watch } = useForm<FormData>({
    defaultValues: { name: "", description: "" },
  });

  const [addSubtask, { isLoading: isLoadingAdd }] = useAddSubtaskMutation();
  const [updateSubtask, { isLoading: isLoadingUpdate }] =
    useUpdateSubtaskMutation();

  const isLoading = isLoadingAdd || isLoadingUpdate;
  const nameValue = watch("name");
  const nameLength = (nameValue || "").trim().length;
  const nameTooLong = nameLength > SUBTASK_TITLE_MAX;

  const onSubmit = async (data: FormData) => {
    const name = data.name.trim();
    const safeDescription = DOMPurify.sanitize(data.description || "");

    const titleProblem = subtaskTitleProblem(name);
    if (titleProblem) {
      toast(titleProblem, {
        type: "error",
      });
      return;
    }

    if (isLocalMode && onSubmitLocal) {
      onSubmitLocal({
        id: defaultValue?.id,
        name,
        description: safeDescription,
      });
      return;
    }

    try {
      if (defaultValue?.id) {
        const timeBudget =
          (defaultValue as WorkTemplateTaskModel).timeBudget ??
          (defaultValue as WorkTemplateTaskModel).budgetedTime ??
          0;

        await updateSubtask({
          ...(defaultValue as WorkTemplateTaskModel),
          name,
          description: safeDescription,
          timeBudget,
        }).unwrap();

        toast("Subtask successfully updated", {
          type: "success",
        });
      } else {
        if (!id || id === "add") {
          toast("Template should be created first", {
            type: "error",
          });
          return;
        }

        await addSubtask({
          name,
          description: safeDescription,
          workTemplateId: id,
          timeBudget: 0,
        }).unwrap();

        toast("Subtask successfully created", {
          type: "success",
        });
      }

      await getTemplate?.();
      onClose();
    } catch (e) {
      toast(serverErrorMessage(e, "Something went wrong"), {
        type: "error",
      });
    }
  };

  useEffect(() => {
    reset({
      name: defaultValue?.name || "",
      description: defaultValue?.description || "",
    });
  }, [defaultValue, reset]);

  return (
    <div
      className={
        "transitions-all mb-2 grid w-full grid-cols-[20px_minmax(24px,max-content)_1fr_72px] gap-4 rounded-xl border border-[#6938EF] bg-[#7A5AF81A] p-3 shadow-settings-purple duration-300 last:mb-0"
      }>
      <div className={"text-[#253747]"}>
        <DraggableSvg />
      </div>

      <div
        className={
          "flex h-6 w-fit min-w-[24px] items-center justify-center rounded-full bg-[#6938EF] px-2 text-sm text-white"
        }>
        {order}
      </div>

      <div className={"min-w-0"}>
        <div className={"mb-1.5"}>
          <Controller
            control={control}
            name={"name"}
            render={({ field: { value, onChange } }) => (
              <input
                type={"text"}
                value={value}
                onChange={(event) => onChange(event.target.value)}
                maxLength={
                  // An older, longer title can still be shortened.
                  Math.max(SUBTASK_TITLE_MAX, (value || "").length)
                }
                aria-invalid={nameTooLong}
                className={
                  "w-full rounded-lg bg-white px-2 py-1 text-base font-semibold text-[#1D2C3B] placeholder:text-[#3F5266] focus:outline-none"
                }
                placeholder={"Add subtask title..."}
              />
            )}
          />
          {nameLength >= SUBTASK_TITLE_MAX - 10 && (
            <div
              className={`mt-1 text-xs ${
                nameLength >= SUBTASK_TITLE_MAX
                  ? "font-semibold text-[#DC2626]"
                  : "text-[#5B6478]"
              }`}>
              {nameTooLong
                ? `Subtask titles can be at most ${SUBTASK_TITLE_MAX} characters (now ${nameLength}).`
                : nameLength === SUBTASK_TITLE_MAX
                ? `Maximum ${SUBTASK_TITLE_MAX} characters reached.`
                : `${nameLength}/${SUBTASK_TITLE_MAX} characters`}
            </div>
          )}
        </div>

        <div>
          <Controller
            control={control}
            name={"description"}
            render={({ field: { value, onChange } }) => (
              <RichText
                value={value || ""}
                onChange={onChange}
                outerHeight={140}
                placeholder={"Add a description (optional)"}
              />
            )}
          />
        </div>
      </div>

      <div className={"flex items-end"}>
        <div className={"flex gap-2"}>
          <SettingsIconButton
            icon={<CheckSvg />}
            onClick={() => handleSubmit(onSubmit)()}
            size={"small"}
            colorVariant={"purple"}
            disabled={isLoading || !nameValue?.trim() || nameTooLong}
          />

          <SettingsIconButton
            icon={<CloseSvg />}
            onClick={onClose}
            size={"small"}
            disabled={isLoading}
          />
        </div>
      </div>
    </div>
  );
};
